import React, { useEffect, useMemo, useRef, useState } from "react";
import { isDuplicateFontFlag } from "../../services/customFontsApi";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FontIcon from "../../images/FontIcon-img.svg";
import "./UploadedFontsList.scss";
import DeleteFontIcon from "../../images/deleteFontIconImage.svg";

const FONT_LABEL_MAX_LENGTH = 35;
const FONT_LABEL_MAX_LENGTH_WITH_REPLACE = 15;
const FONT_LABEL_MAX_LENGTH_PENDING = 18;

function truncateFontLabel(label, maxLength = FONT_LABEL_MAX_LENGTH) {
  const text = String(label ?? "");
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

export default function UploadedFontsList({
  fontRecords = [],
  pendingUploads = [],
  onDeleteFontRecord,
  onReplaceFontRecord,
  disabled = false,
  onRetryFailedUpload,
  onDeleteFailedUpload
}) {
  const scrollRef = useRef(null);
  const thumbRef = useRef(null);

  const [thumbHeight, setThumbHeight] = useState(40);
  const [thumbTop, setThumbTop] = useState(0);

  const sortedFontRecords = useMemo(() => {
    return [...fontRecords].sort((a, b) => {
      const aDup = isDuplicateFontFlag(a) ? 0 : 1;
      const bDup = isDuplicateFontFlag(b) ? 0 : 1;
      return aDup - bDup;
    });
  }, [fontRecords]);

  const updateScrollbar = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollHeight, clientHeight, scrollTop } = el;
    const scrollable = Math.max(scrollHeight - clientHeight, 0);

    if (scrollable <= 0) {
      setThumbHeight(clientHeight);
      setThumbTop(0);
      return;
    }

    const ratio = clientHeight / scrollHeight;
    const newThumbHeight = Math.max(
      Math.min(ratio * clientHeight, clientHeight),
      20,
    );
    const maxThumbTop = Math.max(clientHeight - newThumbHeight, 0);
    const newThumbTop = (scrollTop / scrollable) * maxThumbTop;

    setThumbHeight(newThumbHeight);
    setThumbTop(newThumbTop);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    updateScrollbar();

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        updateScrollbar();
      });
    });
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, []);

  const onScroll = () => updateScrollbar();

  const handleThumbMouseDown = (e) => {
    const startY = e.clientY;
    const startTop = thumbTop;

    const onMouseMove = (moveEvent) => {
      const el = scrollRef.current;
      if (!el) return;

      const { scrollHeight, clientHeight } = el;
      const scrollable = Math.max(scrollHeight - clientHeight, 0);
      if (scrollable <= 0) return;

      const ratio = clientHeight / scrollHeight;
      const thumbH = Math.max(Math.min(ratio * clientHeight, clientHeight), 20);
      const maxThumbTop = Math.max(clientHeight - thumbH, 0);
      const delta = moveEvent.clientY - startY;

      let newTop = startTop + delta;
      newTop = Math.max(0, Math.min(newTop, maxThumbTop));

      const scrollRatio = maxThumbTop > 0 ? newTop / maxThumbTop : 0;
      el.scrollTop = scrollRatio * scrollable;
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  if (!fontRecords.length && !pendingUploads.length) {
    return (
      <div className="uploaded-fonts-list uploaded-fonts-list--empty">
        <div className="uploaded-fonts-empty">
          <InfoOutlinedIcon style={{ width: 16, height: 16 }} />
          Upload a font to use it in your fonts list.
        </div>
      </div>
    );
  }

  return (
    <div className="uploaded-fonts-list">
      <div className="uploaded-fonts-title">Uploaded Font</div>

      <div className="uploaded-fonts-scroll-wrapper">
        {/* Scroll Area */}
        <div
          className="uploaded-fonts-list-scroll"
          ref={scrollRef}
          onScroll={onScroll}
        >
          <ul className="uploaded-fonts-list-items">
            {pendingUploads.map((pending) => (
              <li
                key={pending.id}
                className="uploaded-fonts-list-item uploaded-fonts-list-item--pending"
              >
                <div className="font-list-item-container">
                  <div className="uploaded-fonts-left">
                    <img src={FontIcon} alt={pending.label} />
                    <span
                      className="uploaded-fonts-list-label uploaded-fonts-list-label--pending"
                      title={pending.label}
                    >
                      {truncateFontLabel(
                        pending.label,
                        FONT_LABEL_MAX_LENGTH_PENDING,
                      )}
                    </span>
                    {pending.status === "failed" && 
                      <span className="uploaded-fonts-failed-tag">
                        Failed
                      </span>
                    }
                  </div>
                </div>
                {pending.status === "failed" ? (
                  <div className="uploaded-fonts-actions">
                    {/* <Button
                      type="button"
                      size="small"
                      className="uploaded-fonts-replace-btn"
                      onClick={() => onRetryFailedUpload?.(pending)}
                    >
                      REPLACE
                    </Button> */}

                    <IconButton
                      size="small"
                      onClick={() => onDeleteFailedUpload?.(pending.id)}
                      aria-label={`Delete ${pending.label}`}
                    >
                      <img src={DeleteFontIcon} alt="delete" />
                    </IconButton>
                  </div>
                ) :
                ( <div className="uploaded-fonts-upload-progress">
                  <div className="uploaded-fonts-progress-track">
                    <div
                      className="uploaded-fonts-progress-fill"
                      style={{ width: `${pending.progress}%` }}
                    />
                  </div>

                  {/* uploading... / Processing font... / 99.00%  progress bar */}
                  <span className="uploaded-fonts-progress-text">
                    {pending.progressText ??
                      `${pending.progress}% Uploading...`}
                  </span>
                </div> )
                }
              </li>
            ))}
            {sortedFontRecords.map((record) => {
              const isDuplicate = isDuplicateFontFlag(record);
              const labelMaxLength = isDuplicate
                ? FONT_LABEL_MAX_LENGTH_WITH_REPLACE
                : FONT_LABEL_MAX_LENGTH;
              
              const isPendingFont = !isDuplicate && pendingUploads.some(
                (font) => !font.isPotentialDuplicate && font.label.replace(/\.(ttf|otf)$/i, "") === record.label
              );
              if (isPendingFont) return null;
              
              return (
                <li key={record.id} className={"uploaded-fonts-list-item"}>
                  <div className="uploaded-fonts-left">
                    <img src={FontIcon} alt={record.label} />

                    <span
                      className="uploaded-fonts-list-label"
                      style={{
                        fontFamily: `"${record.familyName}", sans-serif`,
                      }}
                      title={record.label}
                    >
                      {truncateFontLabel(record.label, labelMaxLength)}
                    </span>
                    <span>
                      {isDuplicate ? (
                        <span className="uploaded-fonts-duplicate-tag">
                          Font is already available.
                        </span>
                      ) : null}
                    </span>
                  </div>

                  <div className="uploaded-fonts-actions">
                    {/* {isDuplicate ? (
                      <Button
                        type="button"
                        size="small"
                        className="uploaded-fonts-replace-btn"
                        disabled={disabled}
                        onClick={() => onReplaceFontRecord?.(record)}
                      >
                        REPLACE
                      </Button>
                    ) : null} */}
                    <IconButton
                      size="small"
                      disabled={disabled}
                      onClick={() => onDeleteFontRecord?.(record)}
                      aria-label={`Delete ${record.label}`}
                    >
                      <img src={DeleteFontIcon} alt="delete" />
                    </IconButton>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Custom Scrollbar */}
        <div className="custom-scrollbar-track">
          <div
            className="custom-scrollbar-thumb"
            ref={thumbRef}
            style={{
              height: `${thumbHeight}px`,
              transform: `translateY(${thumbTop}px)`,
            }}
            onMouseDown={handleThumbMouseDown}
          />
        </div>
      </div>
    </div>
  );
}
